import React, { ReactNode } from 'react'
import { useCookieConsent, ConsentType } from '../../hooks/useCookieConsent'

interface ConsentGateProps {
  type: ConsentType
  serviceName: string
  controllerName: string
  description: string
  children: ReactNode
  className?: string
}

/**
 * ConsentGate - GDPR compliant wrapper for third-party embeds
 * 
 * Blocks content until user explicitly accepts or rejects cookies.
 * Displays an informative notice with clear options for consent.
 * 
 * @param type - The consent category (google, microsoft, youtube, instagram, social-media)
 * @param serviceName - Human-readable service name (e.g., "Google Forms")
 * @param description - Brief description of what cookies are used for
 * @param children - The content to show after consent is given
 */
const ConsentGate: React.FC<ConsentGateProps> = ({
  type,
  serviceName,
  controllerName,
  description,
  children,
  className = ''
}) => {
  const {
    hasConsent,
    hasDecided,
    acceptConsent,
    rejectConsent,
    isLoaded
  } = useCookieConsent()

  // Show loading state while checking localStorage
  if (!isLoaded) {
    return (
      <div className={`my-6 ${className}`}>
        <div className="bg-surface border border-border rounded-lg p-8 text-center">
          <div className="text-text-light">Loading...</div>
        </div>
      </div>
    )
  }

  // If consent is given, render the children
  if (hasConsent(type)) {
    return <>{children}</>
  }

  // If user has decided (either accepted or rejected), show appropriate message
  if (hasDecided(type)) {
    return (
      <div className={`my-6 ${className}`}>
        <div className="bg-surface border border-border rounded-lg p-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-4">
              <svg
                className="w-12 h-12 mx-auto text-text-light"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-heading font-semibold text-text mb-2">
              Content Blocked
            </h3>
            <p className="text-text-light mb-4">
              You have chosen not to accept cookies from {controllerName}. This content cannot be displayed without your consent.
            </p>
            <button
              onClick={() => acceptConsent(type)}
              className="btn btn-primary"
            >
              Accept Cookies & Load Content
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show consent request for first-time users
  return (
    <div className={`my-6 ${className}`}>
      <div className="bg-surface border-2 border-accent/30 rounded-lg p-8 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-heading font-semibold text-primary mb-2">
                ⚠️ Cookie Consent Required
              </h3>
              <p className="text-text mb-3">
                This page contains embedded content from <strong>{serviceName}</strong>.
              </p>
              <p className="text-text-light text-sm mb-4">
                {description}
              </p>
              <div className="bg-background border border-border rounded p-3 mb-4">
                <p className="text-sm text-text-light">
                  <strong className="text-text">What this means:</strong> By accepting, you allow {controllerName} to set cookies on your device. 
                  These cookies may be used for analytics, personalization, and tracking purposes. 
                  You can change your preference at any time.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col-reverse sm:flex-row gap-3 justify-center">
            <button
              onClick={() => rejectConsent(type)}
              className="btn btn-secondary px-6 py-3 flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Reject Cookies
            </button>
            <button
              onClick={() => acceptConsent(type)}
              className="btn btn-primary px-6 py-3 flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Accept Cookies
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConsentGate
